import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Cpu, ExternalLink, Filter, ListTodo, RefreshCw } from 'lucide-react';
import { api, type PaginatedData } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface AutonomousTaskRecord {
  id: string;
  task_type: string | null;
  title: string | null;
  summary: string | null;
  status: string;
  channel: string | null;
  domain: string | null;
  entity_type: string | null;
  entity_id: string | null;
  requested_by_user_id: string | null;
  assigned_user_id: string | null;
  wait_reason: string | null;
  action_route: string | null;
  result_route: string | null;
  input_data?: Record<string, unknown>;
  output_data?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

const STATUS_OPTIONS = [
  'all',
  'draft',
  'awaiting_input',
  'awaiting_confirmation',
  'awaiting_approval',
  'scheduled',
  'queued',
  'running',
  'waiting_external',
  'completed',
  'failed',
  'cancelled',
] as const;

function getStatusBadgeClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    case 'scheduled':
      return 'bg-blue-100 text-blue-700';
    case 'running':
    case 'queued':
      return 'bg-amber-100 text-amber-700';
    case 'awaiting_input':
    case 'awaiting_confirmation':
    case 'awaiting_approval':
      return 'bg-violet-100 text-violet-700';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function isOnboardingTask(task: AutonomousTaskRecord): boolean {
  return ['onboarding_brief', 'sales_followup', 'delivery_review', 'support_review'].includes(task.task_type ?? '');
}

function getOnboardingTaskLabel(taskType: string | null | undefined): string {
  switch (taskType) {
    case 'onboarding_brief':
      return 'Onboarding Brief';
    case 'sales_followup':
      return 'Sales Follow-up';
    case 'delivery_review':
      return 'Delivery Review';
    case 'support_review':
      return 'Support Review';
    default:
      return taskType ?? '—';
  }
}

function getLinkedOnboardingRecordId(task: AutonomousTaskRecord): string | null {
  const metadata =
    task.metadata && typeof task.metadata === 'object'
      ? (task.metadata as Record<string, unknown>)
      : {};
  if (typeof metadata.onboardingRecordId === 'string' && metadata.onboardingRecordId) {
    return metadata.onboardingRecordId;
  }
  return task.entity_type === 'onboarding-record' && task.entity_id ? task.entity_id : null;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function extractMissingFields(task: AutonomousTaskRecord): string[] {
  const candidates = [task.metadata?.missingFields];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.map((item) => String(item)).filter(Boolean);
    }
  }
  return [];
}

function getLegacyTransferResult(task: AutonomousTaskRecord): Record<string, unknown> | null {
  const output = (task.output_data ?? {}) as Record<string, unknown>;
  const live = output.liveResult;
  const dry = output.dryRunResult;
  if (live && typeof live === 'object') return live as Record<string, unknown>;
  if (dry && typeof dry === 'object') return dry as Record<string, unknown>;
  return null;
}

function getTrackContainerResult(task: AutonomousTaskRecord): Record<string, unknown> | null {
  const output = (task.output_data ?? {}) as Record<string, unknown>;
  const result = output.trackingResult;
  return result && typeof result === 'object' ? (result as Record<string, unknown>) : null;
}

export default function TaskCenterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<AutonomousTaskRecord[]>([]);
  const [selectedTask, setSelectedTask] = useState<AutonomousTaskRecord | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [mineOnly, setMineOnly] = useState<'mine' | 'all'>('mine');
  const [loading, setLoading] = useState(false);
  const [taskActionBusy, setTaskActionBusy] = useState<'save-track' | 'run-track' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [containerInput, setContainerInput] = useState('');
  const queryTaskId = searchParams.get('taskId');

  const taskTypeOptions = useMemo(() => {
    const unique = [...new Set(tasks.map((task) => task.task_type).filter(Boolean))] as string[];
    return ['all', ...unique];
  }, [tasks]);

  const loadTaskDetail = useCallback(async (taskId: string) => {
    try {
      const task = await api.get<AutonomousTaskRecord>(`/autonomous-tasks/${taskId}`);
      setSelectedTask(task);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task detail');
    }
  }, []);

  const loadTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: '100',
      });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter !== 'all') params.set('taskType', typeFilter);
      if (mineOnly === 'mine' && user?.id) params.set('requestedByUserId', user.id);
      const data = await api.get<PaginatedData<AutonomousTaskRecord>>(`/autonomous-tasks?${params.toString()}`);
      setTasks(data.items ?? []);
      setSelectedTask((current) => {
        if (queryTaskId) {
          return data.items?.find((item) => item.id === queryTaskId) ?? current ?? data.items?.[0] ?? null;
        }
        if (!current) return data.items?.[0] ?? null;
        return data.items?.find((item) => item.id === current.id) ?? data.items?.[0] ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [mineOnly, queryTaskId, statusFilter, typeFilter, user?.id]);

  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (queryTaskId && queryTaskId !== selectedTask?.id) {
      void loadTaskDetail(queryTaskId);
    }
  }, [loadTaskDetail, queryTaskId, selectedTask?.id]);

  useEffect(() => {
    if (selectedTask?.task_type === 'track_container') {
      const input = (selectedTask.input_data ?? {}) as Record<string, unknown>;
      setContainerInput(typeof input.containerNo === 'string' ? input.containerNo : '');
      return;
    }
    setContainerInput('');
  }, [selectedTask]);

  const openTask = useCallback((task: AutonomousTaskRecord) => {
    const route = task.result_route || task.action_route;
    if (route && route !== '/admin/tasks' && route !== '/admin/tasks?taskId=' + task.id) {
      navigate(route);
      return;
    }
    setSelectedTask(task);
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('taskId', task.id);
      return next;
    });
  }, [navigate, setSearchParams]);

  const saveTrackContainerTask = useCallback(async () => {
    if (!selectedTask || selectedTask.task_type !== 'track_container') return;
    setTaskActionBusy('save-track');
    try {
      const data = await api.patch<{ task: AutonomousTaskRecord; missingFields: string[] }>(
        `/container-tracking/tasks/${selectedTask.id}/input`,
        {
          containerNo: containerInput || undefined,
        },
      );
      setSelectedTask(data.task);
      await loadTasks();
      toast.success(data.missingFields.length > 0 ? 'Task updated, still waiting for input' : 'Container number saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update tracking task');
    } finally {
      setTaskActionBusy(null);
    }
  }, [containerInput, loadTasks, selectedTask]);

  const executeTrackContainerTask = useCallback(async () => {
    if (!selectedTask || selectedTask.task_type !== 'track_container') return;
    setTaskActionBusy('run-track');
    try {
      const data = await api.post<{ task: AutonomousTaskRecord }>(
        `/container-tracking/tasks/${selectedTask.id}/execute`,
        {},
      );
      setSelectedTask(data.task);
      await loadTasks();
      toast.success('Container tracking task executed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to execute tracking task');
    } finally {
      setTaskActionBusy(null);
    }
  }, [loadTasks, selectedTask]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ListTodo className="h-6 w-6 text-primary" />
            Task Center
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            查看待补资料、待确认、已排程和已完成的 AI / admin tasks
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadTasks()} disabled={loading}>
          <RefreshCw className={cn('h-4 w-4 mr-2', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              Filters
            </div>

            <Select value={mineOnly} onValueChange={(value) => setMineOnly(value as 'mine' | 'all')}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="mine">My Tasks</SelectItem>
                <SelectItem value="all">All Tasks</SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-56">
                <SelectValue placeholder="Task Type" />
              </SelectTrigger>
              <SelectContent>
                {taskTypeOptions.map((taskType) => (
                  <SelectItem key={taskType} value={taskType}>
                    {taskType}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Badge variant="outline">{tasks.length} task(s)</Badge>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Load failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Task Queue</CardTitle>
          </CardHeader>
          <CardContent>
            {tasks.length === 0 ? (
              <div className="text-sm text-muted-foreground">No tasks found for the current filter.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="w-[120px] text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tasks.map((task) => (
                    <TableRow
                      key={task.id}
                      className={cn('cursor-pointer', selectedTask?.id === task.id && 'bg-muted/40')}
                      onClick={() => openTask(task)}
                    >
                      <TableCell className="align-top">
                        <div className="font-medium">{task.title ?? task.task_type ?? task.id}</div>
                        <div className="text-xs text-muted-foreground mt-1">{task.summary ?? '—'}</div>
                        {isOnboardingTask(task) ? (
                          <div className="mt-2 flex flex-wrap gap-2">
                            <Badge variant="outline">Onboarding</Badge>
                            {task.metadata?.escalationTarget ? (
                              <Badge variant="outline">escalate:{String(task.metadata.escalationTarget)}</Badge>
                            ) : null}
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="align-top">
                        <Badge className={getStatusBadgeClass(task.status)}>{task.status}</Badge>
                      </TableCell>
                      <TableCell className="align-top">
                        <div className="text-sm">{isOnboardingTask(task) ? getOnboardingTaskLabel(task.task_type) : task.task_type ?? '—'}</div>
                        <div className="text-xs text-muted-foreground mt-1">{task.channel ?? 'manual'}</div>
                      </TableCell>
                      <TableCell className="align-top text-sm text-muted-foreground">
                        {formatDateTime(task.updated_at ?? task.created_at)}
                      </TableCell>
                      <TableCell className="align-top text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(event) => {
                            event.stopPropagation();
                            openTask(task);
                          }}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Cpu className="h-4 w-4 text-primary" />
              Task Detail
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {!selectedTask ? (
              <div className="text-sm text-muted-foreground">Select a task to inspect its detail.</div>
            ) : (
              <>
                <div>
                  <div className="text-sm font-medium">{selectedTask.title ?? selectedTask.task_type ?? selectedTask.id}</div>
                  <div className="text-xs text-muted-foreground mt-1">{selectedTask.summary ?? '—'}</div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Badge className={getStatusBadgeClass(selectedTask.status)}>{selectedTask.status}</Badge>
                  {selectedTask.task_type ? <Badge variant="outline">{selectedTask.task_type}</Badge> : null}
                  {selectedTask.wait_reason ? <Badge variant="outline">wait:{selectedTask.wait_reason}</Badge> : null}
                  {selectedTask.domain ? <Badge variant="outline">{selectedTask.domain}</Badge> : null}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Requested By</span>
                    <span className="text-right font-mono text-xs">{selectedTask.requested_by_user_id ?? '—'}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Assigned To</span>
                    <span className="text-right font-mono text-xs">{selectedTask.assigned_user_id ?? '—'}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Created</span>
                    <span className="text-right">{formatDateTime(selectedTask.created_at)}</span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-muted-foreground">Updated</span>
                    <span className="text-right">{formatDateTime(selectedTask.updated_at)}</span>
                  </div>
                </div>

                {extractMissingFields(selectedTask).length > 0 ? (
                  <Alert>
                    <AlertTitle>Awaiting Input</AlertTitle>
                    <AlertDescription>
                      Missing: {extractMissingFields(selectedTask).join(', ')}
                    </AlertDescription>
                  </Alert>
                ) : null}

                {isOnboardingTask(selectedTask) ? (
                  <div className="space-y-3 rounded-md border p-3">
                    <div className="text-sm font-medium">Onboarding Routing</div>
                    {getLinkedOnboardingRecordId(selectedTask) ? (
                      <Alert>
                        <AlertTitle>Linked Onboarding Record</AlertTitle>
                        <AlertDescription>{getLinkedOnboardingRecordId(selectedTask)}</AlertDescription>
                      </Alert>
                    ) : null}
                    <div className="grid gap-2 sm:grid-cols-2">
                      <div className="rounded border px-3 py-2 text-sm">
                        <div className="text-xs text-muted-foreground uppercase">Buyer Motion</div>
                        <div className="font-medium">{String((selectedTask.input_data?.buyerMotionLabel ?? selectedTask.input_data?.buyerMotionId ?? '—'))}</div>
                      </div>
                      <div className="rounded border px-3 py-2 text-sm">
                        <div className="text-xs text-muted-foreground uppercase">Identity Stage</div>
                        <div className="font-medium">{String((selectedTask.input_data?.identityStage ?? '—'))}</div>
                      </div>
                      <div className="rounded border px-3 py-2 text-sm">
                        <div className="text-xs text-muted-foreground uppercase">Onboarding Mode</div>
                        <div className="font-medium">{String((selectedTask.input_data?.onboardingMode ?? '—'))}</div>
                      </div>
                      <div className="rounded border px-3 py-2 text-sm">
                        <div className="text-xs text-muted-foreground uppercase">Escalation Target</div>
                        <div className="font-medium">{String((selectedTask.metadata?.escalationTarget ?? 'none'))}</div>
                      </div>
                    </div>
                    {Array.isArray(selectedTask.input_data?.requiredNow) ? (
                      <Alert>
                        <AlertTitle>Required Now</AlertTitle>
                        <AlertDescription>{(selectedTask.input_data!.requiredNow as unknown[]).map((item) => String(item)).join(', ')}</AlertDescription>
                      </Alert>
                    ) : null}
                    {selectedTask.metadata?.creditsMode && typeof selectedTask.metadata.creditsMode === 'object' ? (
                      <Alert>
                        <AlertTitle>Credits Mode</AlertTitle>
                        <AlertDescription>{String((selectedTask.metadata.creditsMode as Record<string, unknown>).label ?? '—')}</AlertDescription>
                      </Alert>
                    ) : null}
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  {getLinkedOnboardingRecordId(selectedTask) ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/onboarding-records?recordId=${encodeURIComponent(getLinkedOnboardingRecordId(selectedTask)!)}`)}
                    >
                      Open Onboarding Record
                    </Button>
                  ) : null}
                  {selectedTask.action_route ? (
                    <Button variant="default" size="sm" onClick={() => navigate(selectedTask.action_route!)}>
                      Open Action
                    </Button>
                  ) : null}
                  {selectedTask.result_route && selectedTask.result_route !== selectedTask.action_route ? (
                    <Button variant="outline" size="sm" onClick={() => navigate(selectedTask.result_route!)}>
                      Open Result
                    </Button>
                  ) : null}
                  {(selectedTask.result_route || selectedTask.action_route) ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openTask(selectedTask)}
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Jump
                    </Button>
                  ) : null}
                </div>

                {selectedTask.task_type === 'legacy_data_transfer' && getLegacyTransferResult(selectedTask) ? (
                  <div className="space-y-3 rounded-md border p-3">
                    <div className="text-sm font-medium">Transfer Result Snapshot</div>
                    {(() => {
                      const result = getLegacyTransferResult(selectedTask)!;
                      const summary = (result.summary ?? {}) as Record<string, unknown>;
                      const syncRun = (result.syncRun ?? {}) as Record<string, unknown>;
                      const dryRun = Boolean(result.dryRun);
                      return (
                        <>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="outline">{dryRun ? 'DRY RUN' : 'LIVE IMPORT'}</Badge>
                            <Badge variant="outline">status: {String(syncRun.status ?? 'unknown')}</Badge>
                            <Badge variant="outline">run: {String(syncRun.id ?? '—')}</Badge>
                          </div>
                          {Object.keys(summary).length > 0 ? (
                            <div className="grid gap-2 sm:grid-cols-2">
                              {Object.entries(summary).slice(0, 8).map(([key, value]) => (
                                <div key={key} className="rounded border px-3 py-2 text-sm">
                                  <div className="text-xs text-muted-foreground uppercase">{key}</div>
                                  <div className="font-medium">{String(value)}</div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-sm text-muted-foreground">No summary available yet.</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                ) : null}

                {selectedTask.task_type === 'track_container' ? (
                  <div className="space-y-3 rounded-md border p-3">
                    <div className="space-y-2">
                      <div className="text-sm font-medium">Track Container Input</div>
                      <Input
                        value={containerInput}
                        onChange={(event) => setContainerInput(event.target.value.toUpperCase())}
                        placeholder="Enter container no"
                      />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => void saveTrackContainerTask()}
                        disabled={taskActionBusy !== null}
                      >
                        Save Input
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => void executeTrackContainerTask()}
                        disabled={taskActionBusy !== null}
                      >
                        Run Lookup
                      </Button>
                    </div>
                    {getTrackContainerResult(selectedTask) ? (
                      <div className="space-y-3 rounded-md border bg-muted/30 p-3">
                        {(() => {
                          const result = getTrackContainerResult(selectedTask)!;
                          const matches = Array.isArray(result.matches) ? (result.matches as Array<Record<string, unknown>>) : [];
                          return (
                            <>
                              <div className="flex flex-wrap gap-2">
                                <Badge variant="outline">found: {String(result.found ?? false)}</Badge>
                                <Badge variant="outline">matches: {String(result.totalMatches ?? matches.length)}</Badge>
                              </div>
                              {matches.length === 0 ? (
                                <div className="text-sm text-muted-foreground">No container/shipment match found.</div>
                              ) : (
                                <div className="space-y-2">
                                  {matches.slice(0, 3).map((match, index) => {
                                    const events = Array.isArray(match.events) ? (match.events as Array<Record<string, unknown>>) : [];
                                    return (
                                      <div key={`${String(match.id ?? index)}`} className="rounded border bg-background p-3 text-sm">
                                        <div className="font-medium">
                                          {String(match.container_no ?? '—')} • Shipment {String(match.shipment_no ?? '—')}
                                        </div>
                                        <div className="mt-1 text-xs text-muted-foreground">
                                          {String(match.vessel ?? '—')} / {String(match.voyage ?? '—')} • {String(match.pol ?? '—')} → {String(match.pod ?? '—')}
                                        </div>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          <Badge variant="outline">container: {String(match.container_status ?? '—')}</Badge>
                                          <Badge variant="outline">shipment: {String(match.shipment_status ?? '—')}</Badge>
                                          <Badge variant="outline">events: {String(events.length)}</Badge>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
